import React from 'react';
import { TEMPLATES } from '@cotera/client/v0/agent/templates';
import { DG } from '@cotera/sdk/core';
import type { RichTextEditorRef } from '@cotera/client/app/components/app/rte';
import { getAgentSeedRuntime } from './seed/runtime';
import {
  ExternalLinkIcon,
  FilterIcon,
  NotepadTextIcon,
  SearchIcon,
  TagsIcon,
} from 'lucide-react';

type TemplateSuggestion = {
  icon: React.ComponentType<Record<string, unknown>>;
  text: string;
  color: string;
  templateKey: keyof typeof TEMPLATES;
};

const AGENT_SUGGESTIONS: TemplateSuggestion[] = [
  {
    icon: SearchIcon,
    text: 'Google Search',
    color: '#76d0eb',
    templateKey: 'Google',
  },
  {
    icon: FilterIcon,
    text: 'Find Mentions',
    color: '#ea8444',
    templateKey: 'FindMentionsOf',
  },
  {
    icon: NotepadTextIcon,
    text: 'Meeting Summary',
    color: '#9b87f5',
    templateKey: 'MeetingSummary',
  },
  {
    icon: TagsIcon,
    text: 'Content Classifier',
    color: '#6c71ff',
    templateKey: 'ContentClassifier',
  },
];

function resolveEditor(
  editorRef: React.RefObject<RichTextEditorRef | null> | undefined,
  agentId: string | undefined
) {
  if (editorRef !== undefined) {
    return editorRef.current?.editor;
  }
  if (agentId !== undefined) {
    return getAgentSeedRuntime(agentId)?.editorRef.current?.editor;
  }
  return undefined;
}

export const AgentTemplateCards: React.FC<{
  editorRef?: React.RefObject<RichTextEditorRef | null>;
  agentId?: string;
}> = ({ editorRef, agentId }) => {
  return (
    <div className="flex flex-col items-center gap-3 mt-1">
      <div className="flex flex-wrap items-center justify-center gap-2">
        {AGENT_SUGGESTIONS.map((suggestion, i) => {
          const { icon: Icon, text, color, templateKey } = suggestion;

          return (
            <button
              key={text}
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm cursor-pointer
              border border-border/50 bg-card/60 text-muted-foreground
              hover:bg-card hover:border-border hover:text-foreground
              transition-all duration-200 animate-in fade-in slide-in-from-left-2 fill-mode-both"
              style={{
                animationDelay: `${200 + i * 75}ms`,
                animationDuration: '300ms',
              }}
              onClick={() => {
                const template = TEMPLATES[templateKey];
                const editor = resolveEditor(editorRef, agentId);
                if (editor !== undefined && editor !== null) {
                  const templateState = new DG.DocumentEditor.EditorState(
                    template
                  );
                  const newState = templateState.setTitle(
                    templateState.title()?.replace('Template: ', '') ?? ''
                  );
                  editor.commands.setContent(newState.getState(), {
                    emitUpdate: true,
                  });
                }
              }}
            >
              <Icon className="h-3.5 w-3.5" style={{ color }} />
              {text}
            </button>
          );
        })}
      </div>
      <a
        href="https://cotera.co/prompts"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1 ml-1 text-xs text-muted-foreground/70 hover:text-foreground transition-colors duration-150
          animate-in fade-in fill-mode-both"
        style={{
          animationDelay: `${200 + AGENT_SUGGESTIONS.length * 75}ms`,
          animationDuration: '300ms',
        }}
      >
        <ExternalLinkIcon className="h-3 w-3" />
        View all templates
      </a>
    </div>
  );
};
